<template>
  <section class="message-wrap">
    <header class="message-header">
      <slot name="header" />
    </header>
    <div class="message-content" ref="content">
      <div class="hr-start" v-if="messages.length !== 0 && isEnd">
        Chat with {{ props.type === 'creator' ? props.merchant.name : props.user.name }} started on
        <span v-if="last_date">{{ moment(last_date).format('MMM DD, YYYY') }}</span>
        <span v-else>{{ moment().format('MMM DD, YYYY') }}</span>
      </div>
      <transition-group class="message-list" name="message-list" tag="div" v-if="messages.length !== 0">
        <el-button v-if="isLoadingMore" class="loading-btn" type="text" v-loading="true"></el-button>
        <template v-for="item in messages" :key="item.message_id">
          {{ greetingTime(item) }}
          <message-item
            :item="item"
            :ajax="ajax"
            :type="props.type"
            :deleted_announcement_ids="props.deleted_announcement_ids.deleted_announcement"
            :hidden_announcements_ids="props.deleted_announcement_ids.hidden_announcements"
            :db_prefix="db_prefix"
            :db_uid="db_uid"
            @retry-message="retryMessage"
            @mark-as-read="markAsRead"
            @open-announcement="openAnnouncement"
            :id="'message-item--' + item.message_id"
          />
        </template>
      </transition-group>
      <no-message v-else-if="!is_loading" :type="props.type" :brand="props.merchant.name" />
      <div class="hr-end" v-if="is_disabled">
        {{
          props.type === 'creator' && props.merchant.chat_status === 'muted'
            ? `Sending messages to ${props.merchant.name} is currently unavailable, but you'll still receive their updates.`
            : props.type === 'creator'
            ? `${props.merchant.name} is not currently accepting messages.`
            : `This conversation is now closed and you can no longer send a direct message to ${props.user.name}.`
        }}
      </div>
      <div class="init-error-message" v-if="initError">
        <svg-icon name="warning" />
        <p>We are working hard to retrieve your chat messages. Please check back at a later time.</p>
      </div>
    </div>

    <footer v-if="!is_disabled" class="message-footer">

      <p class="message-footer-error" v-if="message.length >= 500">Maximum 500 characters</p>
      <span class="error-ico" @click="reconnect" v-if="statusType === 'error'">
        <svg-icon name="retry" /> Reconnect server
      </span>
      <el-input
        v-model="message"
        placeholder="Type your message"
        size="large"
        maxlength="500"
        @keyup.enter="sendMessage"
        :readonly="permission_mes?.chatWithCreator?.permission !== 100"
      />
      <el-button
        size="large"
        :disabled="!message || permission_mes?.chatWithCreator?.permission !== 100"
        @click="sendMessage"
      >
        <svg-icon name="send" />
      </el-button>
    </footer>
  </section>

  <announcement-list
    v-if="props.type === 'creator'"
    :ajax="ajax"
    ref="announcementList"
    :merchant="merchant"
    @open-announcement="openAnnouncement"
  />
  <announcement-detail :ajax="ajax" ref="announcementDetail" :merchant="merchant" />
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { ElInput, ElTooltip, ElButton, ElLoading } from 'element-plus';
import MessageItem from './MessageItem.vue';
import NoMessage from './NoMessage.vue';
import AnnouncementList from './AnnouncementList.vue';
import AnnouncementDetail from './AnnouncementDetail.vue';
import log from '../../utils/Log';
import moment from 'moment';
import Store from '@/store/index.js';
import { CHATROOM_PREFIX, chatStore } from '@/utils/ChatConfig';
let ws = null;
let loading = null;
let pollingInterval = null;
let wsConnectionTimeout = null;
const WS_TIMEOUT = 10000; // 10 seconds timeout for WebSocket connection
const POLLING_INTERVAL = 5000; // 5 seconds polling interval
const HEARTBEAT_INTERVAL = 30000; // Send heartbeat ping every 30 seconds
const HEARTBEAT_TIMEOUT = 10000; // If no pong in 10s, connection is dead
let heartbeatTimer = null;
let lastPongTime = 0;
let isUnmounting = false;
let simulateFailureUntil = 0; // Timestamp until which WS failure simulation is active

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  token: {
    type: String,
    required: true,
  },
  user: {
    type: Object,
    required: true,
  },
  portal_user: {
    type: Object,
    default: () => ({}),
  },
  merchant: {
    type: Object,
    required: true,
  },
  ajax: {
    type: Function,
    required: true,
  },
  is_disabled: {
    type: Boolean,
    default: false,
  },
  deleted_announcement_ids: {
    type: Array,
    default: () => [],
  },
  chatroom_url: {
    type: String,
    required: true,
  },
});

const statusType = ref('connecting');
const is_loading = ref(false);
const initError = ref(false);
const usePolling = ref(false); // Whether to use long polling mode
const messages = ref([]);
const message = ref('');
const content = ref(null);
const announcementList = ref(null);
const announcementDetail = ref(null);
const isEnd = ref(false);
const isLoadingMore = ref(false);
const prefix = CHATROOM_PREFIX;
const db_prefix = ref(prefix);
const ajax = props.ajax;
const emit = defineEmits(['get-message-history', 'mark-as-read', 'disable-chat', 'send-message']);

const uid = props.type + '_' + props.user.id + '_' + props.merchant.id;
const db_uid = ref(uid);
const last_date = ref('');

const permission_mes = computed(() => {
  return Store.state.user_data?.permission_list?.messagesAndAnnouncements;
});

const isDemo = () => !!Store.state.user_data?.merchant_account?.is_demo;

// window.chatStore = chatStore;
// chatStore.clear();
if (!isDemo()) {
  chatStore.getItem(prefix + uid + '_end').then((value) => {
    if (value) {
      isEnd.value = true;
    }
  });
}

let last_active_date = '';
const greetingTime = (item) => {
  // log('green', moment(last_active_date).format('YYYYMMDD') , moment(item.created_at).format('YYYYMMDD'));
  setTimeout(() => {
    if (moment(last_active_date).format('YYYYMMDD') !== moment(item.created_at).format('YYYYMMDD')) {
      last_active_date = item.created_at;
      const el = document.querySelector('#message-item--' + item.message_id);
      const html = `
          <div class="hr-date">
            <time>
              ${moment(item.created_at).format('MMM DD, YYYY')}
            </time>
          </div>
        `;
      if (el && el.classList.contains('is-added') === false) {
        el.classList.add('is-added');
        el.insertAdjacentHTML('beforebegin', html);
      }
    }
  }, 10);
};

const sendWebSocketEvent = (status, eventInfo = null) => {
  const obj = {
    status: status,
    auth_key: props.token,
    timestamp: new Date().getTime(),
  };

  if (eventInfo) {
    obj.detail_log = encodeURIComponent(JSON.stringify(eventInfo));
  }

  ajax.get('/chatroom/add-enter-websocket-history', {
    params: obj,
  });
};

const forceDisconnect = () => {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.close(1000, 'Force disconnect');
    log('red', 'WebSocket connection closed by test');
  }
};

const connectWebSocket = () => {
  // Clear previous timeout timer
  if (wsConnectionTimeout) {
    clearTimeout(wsConnectionTimeout);
    wsConnectionTimeout = null;
  }

  // ws = new WebSocket(props.chatroom_url);
  // Check URL parameter to simulate WebSocket connection failure
  const urlParams = new URLSearchParams(window.location.search);
  // const corruptedUrl = props.chatroom_url.replace(/^(wss?:\/\/)([^.]+)/, '$1$2-invalid-test');
  // log('yellow', 'Simulating WebSocket failure with corrupted URL:', corruptedUrl);
  // ws = new WebSocket(corruptedUrl);
  
  try {
    // When simulate_ws_failure param is present, start a 5-minute simulation window
    if (urlParams.has('simulate_ws_failure') && !simulateFailureUntil) {
      simulateFailureUntil = Date.now() + 5 * 60 * 1000; // 5 minutes
    }

    if (simulateFailureUntil && Date.now() < simulateFailureUntil) {
      const remaining = Math.round((simulateFailureUntil - Date.now()) / 1000);
      const corruptedUrl = props.chatroom_url.replace(/^(wss?:\/\/)([^.]+)/, '$1$2-invalid-test');
      log('yellow', `Simulating WebSocket failure (${remaining}s remaining) with corrupted URL:`, corruptedUrl);
      ws = new WebSocket(corruptedUrl);
    } else {
      if (simulateFailureUntil) {
        log('green', 'WebSocket failure simulation period ended, connecting normally');
        simulateFailureUntil = 0;
      }
      ws = new WebSocket(props.chatroom_url);
    }
  } catch (error) {
    log('red', 'WebSocket initialization error:', error);
    sendWebSocketEvent('init-error', { error: error.message });
    switchToPolling();
    return;
  }

  sendWebSocketEvent('connect');

  loading = ElLoading.service({
    lock: true,
    text: 'Loading Messages...',
    target: document.querySelector('.message-content'),
  });

  is_loading.value = true;
  initError.value = false;
  usePolling.value = false;

  log('green', 'WebSocket connecting');
  statusType.value = 'connecting';

  // Set connection timeout - switch to polling if connection takes too long
  wsConnectionTimeout = setTimeout(() => {
    if (statusType.value === 'connecting') {
      log('red', 'WebSocket connection timeout, switching to polling');
      if (ws) {
        ws.close();
        ws = null;
      }
      switchToPolling();
    }
  }, WS_TIMEOUT);

  ws.onopen = () => onOpen();

  ws.onclose = (event) => onClose(event);

  ws.onerror = (error) => onError(error);

  ws.reconnect = () => onReconnect();

  ws.onreconnect = () => onReconnect();

  ws.onmessage = (event) => onMessage(event);
};

const postMessage = (msg) => {
  if (ws === null || !ws.readyState || ws.readyState !== WebSocket.OPEN) {
    if (ws === null || !ws.readyState) {
      log('green', 'WebSocket msg', msg);
    } else {
      log('red', 'WebSocket is not open:', ws.readyState);

      if (ws.readyState === WebSocket.CLOSED) {
        // reconnect websocket
        log('green', 'Reconnect WebSocket');
        connectWebSocket();
      }
    }

    return;
  }

  if (msg.auth_key) {
    log('blue', 'Sending message:', msg);
    ws.send(JSON.stringify(msg));
  }
};

const sendMessage = () => {
  if (message.value === '') {
    log('red', 'sendMessage error', message.value);
    return;
  }

  log('blue', 'sendMessage', message.value);

  const message_id = generateUUID();

  // html to escape
  message.value = message.value.replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const newMsg = {
    auth_key: props.token,
    message_text: message.value,
    message_type: 'normal-message',
    message_id: message_id,
    client_type: props.type,
  };

  if (props.type === 'merchant') {
    newMsg.merchant_display_name = props.merchant.name;
    newMsg.current_merchant_portal_user_name = props.portal_user.name;
  }

  postMessage(newMsg);

  const date = moment().utc(0).format('YYYY-MM-DD HH:mm:ss');
  const msg = {
    message_id: message_id,
    message_type: 'normal-message',
    message_status: 1,
    avatar: props.type === 'creator' ? props.user.avatar : props.merchant.logo,
    name: props.type === 'creator' ? props.user.name : props.merchant.name,
    message_text: message.value,
    isSelf: true,
    client_type: props.type,
    created_at: date,
    updated_at: date,
    isNew: true,
    isError: false,
  };

  if (props.type === 'merchant') {
    msg.merchant_display_name = props.merchant.name;
    msg.current_merchant_portal_user_name = props.portal_user.name;
  }

  messages.value.push(msg);
  if (!isDemo()) chatStore.setItem(prefix + uid, JSON.stringify(messages.value));
  
  const messageText = message.value;
  message.value = '';

  emit('send-message', msg);

  // Choose sending method based on mode
  if (usePolling.value) {
    // Send via API in polling mode
    sendMessageViaApi({
      message_text: messageText,
      message_id: message_id,
    });
  } else {
    // Send via WebSocket
    postMessage(newMsg);
  }

  // if is mobile, scroll to bottom
  if (window.innerWidth < 668) {
    setTimeout(() => {
      content.value.scrollTop = content.value.scrollHeight;
    }, 100);

    setTimeout(() => {
      content.value.scrollTop = content.value.scrollHeight;
    }, 1000);
  }
};


const syncMessages = (data) => {
  loading.close();
  is_loading.value = false;
  const list = data.data;
  if (!isEnd.value) isEnd.value = list.length < 20;

  if (isEnd.value && !isDemo()) {
    chatStore.setItem(prefix + uid + '_end', 1);
  }

  log('blue', 'Load messages is end', isEnd.value);

  setTimeout(() => {
    isLoadingMore.value = false;

    checkHighlightMessage();
  }, 500);

  if (list.length === 0) {
    return;
  }

  // log('green', 'syncMessages', list);

  list.forEach((item) => {
    if (messages.value.find((msg) => msg.message_id === item.message_id)) {
      // log('red', 'message exists ', item, messages.value.find((msg) => msg.message_id === item.message_id));
      return;
    }

    log('green', 'new message ' + item.message_id);

    const isSelf = item.client_type === props.type;

    log('yellow', 'item', item);

    let content_moderation = null;

    if (item.contentModeration && (item.contentModerationFlagHistory || item.contentModerationFollowUpHistory || item.contentModerationStatusHistory)) {
      content_moderation = item.contentModeration;

      console.log('error', item)

      content_moderation.id = item.contentModerationFlagHistory && item.contentModerationFlagHistory.id ? item.contentModerationFlagHistory.id :
          item.contentModerationFollowUpHistory ? item.contentModerationFollowUpHistory.id :
                              item.contentModerationStatusHistory.id;
      content_moderation.reason = item.contentModerationFlagHistory ? item.contentModerationFlagHistory.reason :
          item.contentModerationFollowUpHistory ? item.contentModerationFollowUpHistory.reason :
                                  item.contentModerationStatusHistory.reason;
      content_moderation.type = item.contentModerationFlagHistory ? 'flag' :
          item.contentModerationFollowUpHistory ? 'follow-up' :
                                'status';
      content_moderation.date = item.contentModerationOriginalCollection ? item.contentModerationOriginalCollection.updated_at :
          item.contentModerationFollowUpHistory ? item.contentModerationFollowUpHistory.updated_at :
                                item.contentModerationOriginalPost.updated_at;
      content_moderation.content = item.contentModerationOriginalCollection ? item.contentModerationOriginalCollection : item.contentModerationOriginalPost;
      content_moderation.flag_status = item.contentModerationFlagHistory ? Number(item.contentModerationFlagHistory.status) : 0;
      content_moderation.status = item.contentModerationStatusHistory ? Number(item.contentModerationStatusHistory.status) : 0;
    }

    messages.value.unshift({
      message_id: item.message_id,
      message_status: item.message_status,
      avatar: item.client_type === 'creator' ? props.user.avatar : props.merchant.logo,
      name: item.client_type === 'creator' ? props.user.name : props.merchant.name,
      message_text: item.message_text,
      isSelf: isSelf,
      client_type: item.client_type,
      updated_at: item.updated_at,
      created_at: item.created_at,
      message_type: item.message_type,
      content_moderation: content_moderation,
      chatroom_announcement_id: item.chatroom_announcement_id ? item.chatroom_announcement_id : 0,
      chatroom_announcement: item.chatroom_announcement ? item.chatroom_announcement : null,
      current_merchant_portal_user_name: item.current_merchant_portal_user_name,
      merchant_display_name: item.merchant_display_name,
    });
  });

  messages.value = messages.value.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  last_date.value = messages.value[0].created_at;
  if (!isDemo()) chatStore.setItem(prefix + uid, JSON.stringify(messages.value));
};

const checkHighlightMessage = () => {

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('message_id')) {
    const url = new URL(window.location);
    const message_id = urlParams.get('message_id');
    url.searchParams.delete('message_id');
    window.history.replaceState(null, '', url.toString());

    setTimeout(() => {
      const targetElement = document.querySelector(`#message-item--${message_id}`);
      if (targetElement && content.value) {
        // scroll to the target element
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        targetElement.classList.add('message-highlight');

        setTimeout(() => {
          targetElement.classList.remove('message-highlight');
        }, 3000);
      }
    }, 500);
  }
};

const onOpen = () => {
  // Clear timeout timer
  if (wsConnectionTimeout) {
    clearTimeout(wsConnectionTimeout);
    wsConnectionTimeout = null;
  }

  log('green', 'WebSocket connected');
  statusType.value = 'connected';
  usePolling.value = false;
  emit('get-message-history');
  postMessage({
    auth_key: props.token,
    message_text: '',
    message_type: 'normal-message',
  });
  sendWebSocketEvent('open');
  startHeartbeat();
};

const startHeartbeat = () => {
  stopHeartbeat();
  lastPongTime = Date.now();
  heartbeatTimer = setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      const elapsed = Date.now() - lastPongTime;
      if (elapsed >= HEARTBEAT_INTERVAL + HEARTBEAT_TIMEOUT) {
        log('red', '[Heartbeat] no server-heart-beat received for', elapsed, 'ms, reconnecting');
        sendWebSocketEvent('heartbeat-timeout');
        stopHeartbeat();
        if (ws) { ws.onclose = null; ws.onerror = null; ws.close(); ws = null; }
        reconnect();
      } else {
        sendWebSocketEvent('heartbeat-ping');
        log('green', '[Heartbeat] connection alive, last server-heart-beat:', elapsed, 'ms ago');
      }
    }
  }, HEARTBEAT_INTERVAL);
};

const stopHeartbeat = () => {
  if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null; }
};

const onClose = (event) => {
  stopHeartbeat();
  // get event info
  const eventInfo = {
    type: event.type,
    isTrusted: event.isTrusted,
    code: event.code,
    reason: event.reason,
    wasClean: event.wasClean,
    target: event.target
      ? {
          readyState: event.target.readyState,
          url: event.target.url,
          protocol: event.target.protocol,
        }
      : null,
    timeStamp: event.timeStamp,
    bubbles: event.bubbles,
    cancelable: event.cancelable,
    defaultPrevented: event.defaultPrevented,
  };

  log('red', 'WebSocket disconnected', event);

  // log('red', 'event json:', JSON.stringify(eventInfo));

  statusType.value = 'closed';
  sendWebSocketEvent('close', eventInfo);

  // Skip recovery if component is being unmounted (e.g. switching chat user)
  if (isUnmounting) return;

  // If not closed cleanly and not already in polling mode, switch to long polling
  if (!event.wasClean && !usePolling.value) {
    log('yellow', 'WebSocket closed unexpectedly, switching to polling');
    switchToPolling();
  } else if (!usePolling.value) {
    // WebSocket closed normally but not in polling mode, try switching to polling
    switchToPolling();
  }
};

const onError = (error) => {
  stopHeartbeat();
  console.error('red', 'WebSocket error', error);
  statusType.value = 'error';
  sendWebSocketEvent('error');

  // Switch to long polling when WebSocket error occurs
  if (!usePolling.value) {
    log('yellow', 'WebSocket error, switching to polling');
    switchToPolling();
  }
  // Errors in polling mode are handled in fetchMessagesViaPolling
};

const onReconnect = () => {
  log('green', 'WebSocket reconnecting');
  statusType.value = 'reconnecting';
};

// Switch to long polling mode
const switchToPolling = () => {
  log('yellow', 'Switching to polling mode');
  usePolling.value = true;
  statusType.value = 'polling';
  initError.value = false;

  // Close WebSocket
  if (ws) {
    ws.close();
    ws = null;
  }

  // Clear timeout timer
  if (wsConnectionTimeout) {
    clearTimeout(wsConnectionTimeout);
    wsConnectionTimeout = null;
  }

  // Close loading if exists
  if (loading) {
    loading.close();
  }
  is_loading.value = false;

  // Fetch messages for the first time
  fetchMessagesViaPolling();

  // Start polling
  startPolling();

  sendWebSocketEvent('switch-to-polling');
};

// Start long polling
const startPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
  }

  pollingInterval = setInterval(() => {
    if (usePolling.value) {
      fetchMessagesViaPolling(false);
    }
  }, POLLING_INTERVAL);

  log('green', 'Polling started with interval:', POLLING_INTERVAL);
};

// Stop long polling
const stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
  log('green', 'Polling stopped');
};

// Fetch messages via API
const fetchMessagesViaPolling = async (showLoading = true) => {
  if (showLoading && loading) {
    loading.close();
  }

  if (showLoading) {
    loading = ElLoading.service({
      lock: true,
      text: 'Loading Messages...',
      target: document.querySelector('.message-content'),
    });
    is_loading.value = true;
  }

  try {
    // In polling mode, emit event to fetch latest messages
    emit('get-message-history');

    if (showLoading) {
      loading.close();
      is_loading.value = false;
    }
  } catch (error) {
    log('red', 'Polling fetch error:', error);
    if (showLoading) {
      loading.close();
      is_loading.value = false;
      // Only show error when polling mode also fails
      initError.value = true;
    }
  }
};

// Send message via API
const sendMessageViaApi = async (messageData) => {
  try {
    const response = await ajax.post('/chatroom/send-new-message', {
      creator_portal_user_ids: [props.user.id],
      message_text: messageData.message_text,
      message_id: messageData.message_id,
      client_type: props.type,
    });

    log('green', 'Message sent via API:', response);

    // Update message status
    const currentMessage = messages.value.find((msg) => msg.message_id === messageData.message_id);
    if (currentMessage) {
      currentMessage.isNew = false;
      currentMessage.isError = false;
    }

    // Fetch latest messages immediately
    fetchMessagesViaPolling(false);

    return response;
  } catch (error) {
    log('red', 'Send message via API error:', error);

    // Mark message as failed
    const currentMessage = messages.value.find((msg) => msg.message_id === messageData.message_id);
    if (currentMessage) {
      currentMessage.isError = true;
    }

    throw error;
  }
};

// Handle page visibility change (background tab throttling)
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    log('green', '[Visibility] Tab became visible');

    // Always fetch latest messages immediately
    emit('get-message-history');

    if (!usePolling.value && ws && ws.readyState === WebSocket.OPEN) {
      // WS looks open — send a heartbeat to verify it's actually alive
      log('green', '[Visibility] Verifying WS connection with heartbeat');
      try {
        ws.send(JSON.stringify({ auth_key: props.token, message_text: '', message_type: 'heartbeat-ping' }));
        startHeartbeat();
      } catch (e) {
        log('red', '[Visibility] WS send failed, reconnecting');
        stopHeartbeat();
        if (ws) { ws.onclose = null; ws.onerror = null; ws.close(); ws = null; }
        reconnect();
      }
    } else if (!usePolling.value && (!ws || ws.readyState !== WebSocket.OPEN)) {
      // WS died while tab was in background
      log('yellow', '[Visibility] WS dead in background, reconnecting');
      reconnect();
    } else if (usePolling.value) {
      // In polling mode — fetch is already emitted above, also restart polling interval
      log('green', '[Visibility] In polling mode, fetched latest messages');
      fetchMessagesViaPolling(false);
    }
  }
};

// Reconnect (try WebSocket first, switch to polling on failure)
const reconnect = () => {
  stopPolling();
  usePolling.value = false;
  initError.value = false;
  connectWebSocket();
};

const onMessage = (event) => {
  log('blue', 'Receive message:', event.data, props.user.id, props.token);

  // Any message from server proves connection is alive
  lastPongTime = Date.now();

  if (event.data) {
    const data = JSON.parse(event.data);
    if (
      (data.client_request_message_type === 'mute-creator' && data.client_type === 'merchant') ||
      data.server_response_message_type === 'get-muted'
    ) {
      log('red', 'Mute creator', data);
      emit('disable-chat', data);
    } else if (data.server_response_message_type === 'send-message-ok' && data.message_text !== '') {
      const isSelf = data.client_type === props.type;

      if (messages.value.find((msg) => msg.message_id === data.message_id)) {
        const currentMessage = messages.value.find((msg) => msg.message_id === data.message_id);
        currentMessage.isNew = false;
        currentMessage.isError = false;
        log('green', 'Message post success', currentMessage.message_id);
      } else {
        if (data.client_type !== props.type) {
          markAsRead([data.message_id]);
        }

        const msg = {
          message_id: data.message_id,
          message_status: data.message_status,
          message_type: data.message_type,
          avatar: data.client_type === 'creator' ? props.user.avatar : props.merchant.logo,
          name: data.client_type === 'creator' ? props.user.name : props.merchant.name,
          message_text: data.message_text,
          isSelf: isSelf,
          client_type: data.client_type,
          updated_at: data.updated_at ? data.updated_at : data.receive_datetime,
          created_at: data.created_at ? data.created_at : data.receive_datetime,
          current_merchant_portal_user_name: data.current_merchant_portal_user_name,
          merchant_display_name: data.merchant_display_name,
          isNew: false,
          isError: false,
        };

        if (data.chatroom_announcement) {
          msg.chatroom_announcement_id = data.chatroom_announcement.id;
          msg.chatroom_announcement = data.chatroom_announcement;
          msg.message_type = 'announcement-message';
        } else if (data.content_moderation) {
          msg.message_type = 'content-moderation-message';
          msg.content_moderation = data.content_moderation;
          msg.content_moderation.id = data.contentModerationFlagHistory ? data.contentModerationFlagHistory.id : data.contentModerationFollowUpHistory ? data.contentModerationFollowUpHistory.id : data.contentModerationStatusHistory.id;
          msg.content_moderation.reason = data.contentModerationFlagHistory ? data.contentModerationFlagHistory.reason : data.contentModerationFollowUpHistory ? data.contentModerationFollowUpHistory.reason : data.contentModerationStatusHistory.reason;
          msg.content_moderation.date = data.contentModerationFlagHistory ? data.contentModerationFlagHistory.updated_at : data.contentModerationFollowUpHistory ? data.contentModerationFollowUpHistory.updated_at : data.contentModerationStatusHistory.updated_at;
          msg.content_moderation.type = data.contentModerationFlagHistory ? 'flag' : data.contentModerationFollowUpHistory ? 'follow-up' : 'status';
          msg.content_moderation.content = data.contentModerationOriginalCollection ? data.contentModerationOriginalCollection : data.contentModerationOriginalPost;
          msg.content_moderation.flag_status = data.contentModerationFlagHistory ? Number(data.contentModerationFlagHistory.status) : 0;
          msg.content_moderation.status = data.contentModerationStatusHistory ? Number(data.contentModerationStatusHistory.status) : 0;
        } else {
          msg.message_type = 'normal-message';
        }

        log('blue', 'Message data', msg);

        messages.value.push(msg);
      }

      if (!isDemo()) chatStore.setItem(prefix + uid, JSON.stringify(messages.value));

      setTimeout(() => {
        // scroll to bottom
        content.value.scrollTop = content.value.scrollHeight;
      }, 100);
    }

    else if (data.server_response_message_type === 'server-heart-beat') {
      log('green', '[Heartbeat] server-heart-beat received');
    }
  }
};

const markAsRead = (ids = []) => {
  log('Violet', 'Mark As Read', ids);

  ids.forEach((id) => {
    const message = messages.value.find((msg) => msg.message_id === id);
    if (message) message.message_status = 100;
  });

  emit('mark-as-read', ids);

  postMessage({
    auth_key: props.token,
    message_type: 'mark-as-read',
    action_message_ids: ids,
  });
};

const retryMessage = (message_id) => {
  const msg = messages.value.find((m) => m.message_id === message_id);

  if (usePolling.value) {
    // Retry sending via API
    sendMessageViaApi({
      message_text: msg.message_text,
      message_id: message_id,
    });
  } else {
    // Retry sending via WebSocket
    postMessage({
      auth_key: props.token,
      message_text: msg.message_text,
      message_type: 'normal-message',
      message_id: message_id,
      client_type: props.type,
    });
  }
};

const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    let r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const handleScroll = () => {
  // log((content.value.scrollHeight - content.value.clientHeight) + content.value.scrollTop);
  if (
    isEnd.value === false &&
    isLoadingMore.value === false &&
    content.value.scrollHeight - content.value.clientHeight + content.value.scrollTop < 10
  ) {
    isLoadingMore.value = true;
    emit('get-message-history', messages.value[0].message_id);
  }
};

const openAnnouncement = (id) => {
  log('green', 'user', props.user);
  announcementDetail.value.open(id, props.user.id);
};

defineExpose({
  syncMessages,
  openAnnouncementList: () => announcementList.value.open(),
  reconnect,
  usePolling,
});

onMounted(() => {
  // connectWebSocket();
  content.value.addEventListener('scroll', handleScroll);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  if (isDemo()) {
    connectWebSocket();
  } else {
    chatStore.getItem(prefix + uid).then((value) => {
      // log('get messages');
      if (value) {
        const items = JSON.parse(value);

        // init messages, set avatar and name for display
        items.forEach((item) => {
          const isSelf = item.client_type === props.type;
          item.isSelf = isSelf;
          item.avatar = item.client_type === 'creator' ? props.user.avatar : props.merchant.logo;
          item.name = item.client_type === 'creator' ? props.user.name : props.merchant.name;
        });

        messages.value = items;
      }

      connectWebSocket();
    });
  }
});

onUnmounted(() => {
  isUnmounting = true;
  if (content.value) content.value.removeEventListener('scroll', handleScroll);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  stopHeartbeat();
  if (ws) {
    ws.close();
    ws = null;
  }
  // Clean up polling
  stopPolling();
  // Clean up timeout timer
  if (wsConnectionTimeout) {
    clearTimeout(wsConnectionTimeout);
    wsConnectionTimeout = null;
  }
});
</script>

<style lang="scss">
.message-wrap {
  flex: 1;
  display: grid;
  grid-template-rows: auto 1fr auto;
  background-color: var(--Bg-01);

  .message-header {
    background-color: var(--Bg-light);
  }

  .message-content {
    flex: 1;
    overflow-y: auto;
    background-color: var(--Bg-01);
    display: flex;
    flex-direction: column-reverse;
    position: relative;
    mask: linear-gradient(0deg, rgba(0, 0, 0, 1) calc(100% - 20px), rgba(0, 0, 0, 0) 100%);

    &.#{$prefix}-loading-parent--relative {
      overflow: hidden;
    }

    .message-list {
      padding: 24px 32px;
      position: relative;
      display: flex;
      flex-direction: column;
      background-color: var(--Bg-01);

      // .hr {
      //   border-top: 1px solid var(--Black-20);
      //   margin: 24px 0;
      //   overflow: hidden;
      //   clear: both;
      // }
    }

    .hr-date {
      text-align: center;
      margin: 24px 0;
      text-align: center;

      time {
        font: var(--medium-12);
        color: var(--Text-default);
        padding: 0 8px;
      }
    }

    .hr-start {
      text-align: center;
      margin: 24px 32px;
      text-align: center;
      font: var(--book-16);
      color: var(--Text-primary);
      order: 1;
      flex: 1;

      &::after {
        content: '';
        display: block;
        width: 100%;
        height: 1px;
        background-color: var(--Black-20);
        margin-top: 24px;
      }
    }

    .hr-end {
      text-align: center;
      margin: 24px 32px;
      text-align: center;
      font: var(--book-16);
      color: var(--Text-primary);
      order: -1;

      &::before {
        content: '';
        display: block;
        width: 100%;
        height: 1px;
        background-color: var(--Black-20);
        margin-bottom: 24px;
      }
    }

    .loading-btn {
      // position: absolute;
      // left: 0;
      // top: 24px;
      // width: 100%;
      position: relative;
      margin: 24px 0;

      .#{$prefix}-loading-mask {
        background-color: transparent;
      }
    }


  }

  .init-error-message {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    color: var(--Text-primary);
    background-color: var(--Red-10);
    position: sticky;
    top: calc(100% - 40px);
    width: 100%;

    svg {
      width: 24px;
      height: 24px;
      color: var(--Red-30);
    }

    p {
      flex: 1;
      padding-left: 12px;
    }
  }

  .message-footer {
    display: flex;
    align-items: center;
    padding: 16px 0;
    background-color: var(--Bg-light);

    .error-ico {
      color: var(--Red-30);
      cursor: pointer;
      margin-right: 16px;
      font: var(--medium-12);
      white-space: nowrap;
    }

    .message-footer-error {
      color: var(--Red-30);
      font-size: 12px;
      line-height: 16px;
    }

    .#{$prefix}-button {
      margin-left: 6px;
      padding: 0;
      width: 42px;
      height: 40px;
      border: none !important;
      color: var(--Text-primary);

      svg {
        height: 20px;
        width: 20px;
      }

      &.is-disabled {
        color: var(--Black-40);
        background-color: var(--Bg-light);
      }
    }
  }

  .#{$prefix}-loading-spinner .#{$prefix}-loading-text {
    color: var(--Text-secondary);
    font: var(--book-12);
  }
}

.message-list-enter-active,
.message-list-leave-active {
  transition: all 0.5s ease;
}

.message-list-enter-from,
.message-list-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.welcome-card {
  transition: outline 0.3s ease-in-out;
}

.message-highlight {
  animation: highlight-pulse .8s ease-in-out;

  .welcome-card {
    outline: outset #cf9494;
  }
}

@keyframes highlight-pulse {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }

  100% {
    transform: scale(1);
  }
}
</style>
